import { Validations } from '@/validations.js';
import { createContact } from '@/hubspot.js';
import { generateUUID } from '@/lib/uuid.js';
import { apiFetch } from '@/utils/api.js';

export const actions = {
    /*
	------------------------------------------------------------------------
	UI actions
	------------------------------------------------------------------------
	*/
    toggleDocGen() {
        this.triggerDocGen = !this.triggerDocGen;
    },
    resetLoadingMessage() {
        this.loading_message = this.loading_message_default;
    },

    /*
	------------------------------------------------------------------------
	Product management actions
	------------------------------------------------------------------------
	*/
    addProduct(productData) {
        const productId = generateUUID();
        // Auto-select first lumen output if dataExtended is available
        const selectedLumenOutput =
            productData.selectedLumenOutput ||
            (productData.dataExtended && productData.dataExtended.length > 0 ? productData.dataExtended[0].output : '');

        this.user.products.push({
            id: productId,
            product: productData.product || '',
            shape: productData.shape || '', // Shape selection for products with shape variants (e.g., Tessell)
            size: productData.size || '',
            size_variant: productData.size_variant || '',
            metric_sabins: productData.metric_sabins || '',
            data: productData.data || {},
            dataExtended: productData.dataExtended || [],
            selectedLumenOutput: selectedLumenOutput,
            manualUnits: productData.manualUnits || null,
            calculation: productData.calculation || null, // Per-product calculation from batch API
        });
        // Set newly added product as active
        this.user.activeProductId = productId;
    },
    setActiveProduct(productId) {
        // Verify product exists before setting as active
        const exists = this.user.products.some((p) => p.id === productId);
        if (exists) {
            this.user.activeProductId = productId;
        }
    },
    updateProduct(id, updates) {
        const index = this.user.products.findIndex((p) => p.id === id);
        if (index !== -1) {
            this.user.products[index] = { ...this.user.products[index], ...updates };
        }
    },
    updateCurrentProduct(updates) {
        // RECURSION PREVENTION: Use activeProductId directly instead of accessing currentProduct getter
        //
        // CRITICAL: The currentProduct getter creates a reactive dependency on user.products array.
        // When updateProduct() mutates user.products[index], it triggers reactive updates that can
        // cause the currentProduct getter to re-evaluate, which would then trigger updateCurrentProduct()
        // again → infinite loop.
        //
        // By using activeProductId directly (a simple string value), we avoid creating a reactive
        // dependency on the products array, breaking the circular dependency chain.
        //
        // Fallback behavior: If activeProductId is not set, use first product (index 0) as fallback.
        // This matches the behavior expected by ProductCard watchers when activeProductId is null.
        const productId = this.user.activeProductId || this.user.products[0]?.id;
        if (productId) {
            this.updateProduct(productId, updates);
        }
    },
    async removeProduct(id) {
        // If removing the active product, reset activeProductId
        if (this.user.activeProductId === id) {
            this.user.activeProductId = null;
        }
        this.user.products = this.user.products.filter((p) => p.id !== id);

        // Trigger recalculation after product removal
        if (this.user.products.length > 0) {
            await this.calculateProductsBatch();
        } else {
            // No products left - still trigger canvas redraw to clear product display
            this.canvasNeedsRedraw = true;
        }
    },
    clearProducts() {
        this.user.products = [];
        this.user.activeProductId = null;
    },
    /*
    ------------------------------------------------------------------------
    Reset products to original selection.

    Removes all additional products (keeps only primary at index 0) and
    resets the primary product's manual units to null (use calculated
    recommendation). Triggers recalculation after reset.
    ------------------------------------------------------------------------
    */
    async resetProducts() {
        $log.log('[store-actions:resetProducts] Called');

        if (!this.user.products || this.user.products.length === 0) {
            return;
        }

        // Keep only the primary product (first in array)
        const primaryProduct = this.user.products[0];

        // Reset primary product's manual units to null (use calculated recommendation)
        primaryProduct.manualUnits = null;

        // Remove all additional products by keeping only the primary
        this.user.products = [primaryProduct];

        // Reset activeProductId to primary product
        this.user.activeProductId = primaryProduct.id;

        // Recalculate to get fresh calculations (this also sets canvasNeedsRedraw)
        await this.calculateProductsBatch();

        $log.log('[store-actions:resetProducts] Products reset to original selection and calculated quantity');
    },

    /*
	------------------------------------------------------------------------
	Calculation actions
	------------------------------------------------------------------------
	*/
    // Batch calculation for all products
    //
    // RECURSION PREVENTION: Core of the recursion prevention system.
    // Updates to product.calculation trigger Vue reactivity, causing watchers in RoomCanvas.vue
    // to fire, potentially creating: calculateProductsBatch() -> updates -> watchers fire
    // -> resizeCanvas() -> checkReadyToCalc() -> calculateProductsBatch() -> ... (loop)
    //
    // Prevention: Guards check isCalculating flags, flag is set before updates and cleared
    // after 500ms delay, only update if calculation changed. See RoomCanvas.vue for watcher guards.
    //
    async calculateProductsBatch() {
        $log.log('[store-actions:calculateProductsBatch] Called');

        // RECURSION PREVENTION: Check flag at start to prevent re-entry
        // Prevents multiple simultaneous API calls and race conditions
        if (this.isCalculating) {
            $log.log('[store-actions:calculateProductsBatch] Skipped: calculation already in progress');
            return;
        }

        // Skip if no room data or no products
        if (!this.user.room || !this.user.products || this.user.products.length === 0) {
            $log.log('[store-actions:calculateProductsBatch] Skipped: no room data or no products');
            return;
        }

        // Skip if room dimensions or materials are not set
        if (
            !this.user.room.width ||
            !this.user.room.length ||
            !this.user.room.height ||
            !this.user.room.usagetarget ||
            !this.user.ceiling_nrc ||
            !this.user.floor_nrc ||
            !this.user.walls_nrc
        ) {
            $log.log('[store-actions:calculateProductsBatch] Skipped: missing room dimensions or materials');
            return;
        }

        // Skip if report validation fails (missing size, variant, or lumen output)
        // This prevents loading overlay from showing until all fields are valid
        if (!this.stepReportIsValid) {
            $log.log('[store-actions:calculateProductsBatch] Skipped: report validation failed (missing required fields)');
            return;
        }

        // RECURSION PREVENTION: Set flag before store updates to block watchers
        // Checked by resizeCanvas(), checkReadyToCalc(), and watchers in RoomCanvas.vue
        // Cleared after 500ms delay (below) to let Vue reactivity settle
        this.isCalculating = true;

        // Build products array for batch API
        const products = this.user.products.map((product) => ({
            id: product.id,
            metricSabins: product.metric_sabins,
            manualUnits: product.manualUnits,
        }));

        // Build payload for batch API
        const payload = {
            targetRoomT60: this.user.room.usagetarget,
            roomUnit: this.user.room.unit,
            roomLength: this.user.room.length,
            roomWidth: this.user.room.width,
            roomHeight: this.user.room.height,
            ceilingNrc: this.user.ceiling_nrc,
            floorNrc: this.user.floor_nrc,
            wallsNrc: this.user.walls_nrc,
            products: products,
        };

        try {
            $log.log('[store-actions:calculateProductsBatch] Sending API request with payload:', payload);

            const response = await apiFetch('/wp-json/lat/v1/calculate-batch', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status}`);
            }

            const result = await response.json();
            $log.log('[store-actions:calculateProductsBatch] Received API response:', result);

            // Update room baseline
            this.roomBaseline = result.roomBaseline;
            $log.log('[store-actions:calculateProductsBatch] Updated roomBaseline:', this.roomBaseline);

            // Update combined calculation
            this.combinedCalculation = result.combined;
            $log.log('[store-actions:calculateProductsBatch] Updated combinedCalculation:', this.combinedCalculation);

            // Update per-product calculations
            //
            // RECURSION PREVENTION: Batch updates and only update if calculation changed
            // Minimizes reactivity triggers: batches updates, checks for changes, applies at once.
            // The isCalculating flag (set above) blocks watchers from reacting to these updates.
            //
            const updates = [];
            this.user.products.forEach((product) => {
                if (result.products[product.id]) {
                    const newCalculation = result.products[product.id];
                    // Only update if calculation actually changed to prevent unnecessary reactivity
                    const currentCalcStr = JSON.stringify(product.calculation || {});
                    const newCalcStr = JSON.stringify(newCalculation);
                    if (currentCalcStr !== newCalcStr) {
                        updates.push({ product, calculation: newCalculation });
                    }
                }
            });

            // Apply all updates at once to minimize reactivity cycles
            updates.forEach(({ product, calculation }) => {
                product.calculation = calculation;
                $log.log(`[store-actions:calculateProductsBatch] Updated calculation for product ${product.id}:`, calculation);
            });

            $log.log('[store-actions:calculateProductsBatch] Completed successfully');

            // RECURSION PREVENTION: Delay clearing flag to let Vue reactivity settle
            //
            // CRITICAL: Watchers with flush:'post' run AFTER DOM updates (asynchronously).
            // If we clear isCalculating immediately, watchers might fire before reactivity settles:
            //   calculateProductsBatch() -> updates -> clears flag -> watchers fire -> resizeCanvas() -> loop
            //
            // 500ms delay ensures:
            // - All Vue reactivity cycles complete
            // - Watchers with flush:'post' have fired (or been blocked)
            // - debouncedUpdate (200ms delay) has settled
            // - DOM has fully updated
            //
            await new Promise((resolve) => {
                setTimeout(() => {
                    this.isCalculating = false;
                    // Signal canvas to redraw after calculation completes
                    this.canvasNeedsRedraw = true;
                    resolve();
                }, 500);
            });

            return result;
        } catch (error) {
            console.error('Batch calculation error:', error);
            $log.log('[store-actions:calculateProductsBatch] Error:', error);

            // Clear flag immediately on error
            this.isCalculating = false;

            return null;
        }
    },
    // Update manual units for a product
    // Recalculation is triggered by RoomCanvas watchers, not here
    updateProductManualUnits(productId, units) {
        const product = this.user.products.find((p) => p.id === productId);
        if (product) {
            product.manualUnits = units;
        }
    },

    /*
	------------------------------------------------------------------------
	Data management actions
	------------------------------------------------------------------------
	*/
    handleClearData(step, msg) {
        $log.log(`[store-actions:handleClearData] Step: ${step}`);

        msg = msg || 'Are you sure you would like to clear the data in this form?';
        const result = confirm(msg);
        // $log.log(`result : ${JSON.stringify(result, null, 4)}`);
        if (result) {
            this.clearDataStep(step);
        }
    },
    clearDataStep: function (step) {
        switch (step) {
            case 'Start':
                this.user.reset = true; // tells the app localstorage has been reset. Primarily used to kow where to route when the app loads.
                this.user.firstname = '';
                this.user.lastname = '';
                this.user.email = '';
                this.user.company = '';
                this.user.user_id = '';
                this.user.company_id = '';
                break;
            case 'Project':
                this.user.projectname = '';
                this.user.roomname = '';
                break;
            case 'Room':
                this.syncRoomData = true; // tell the app to sync the room data back to the form data.
                this.user.room.width = 0;
                this.user.room.length = 0;
                this.user.room.height = 0;
                this.user.room.materials = [];
                this.user.room.usage = '';
                this.clearProducts();
                break;
            case 'Product':
                this.clearProducts();
                break;
            case 'Report':
                this.clearProducts();
                break;
            default:
                break;
        }
    },
    clearDataAll: function () {
        this.clearDataStep('Start');
        this.clearDataStep('Project');
        this.clearDataStep('Room');
        this.clearDataStep('Product');
        this.clearDataStep('Report');
    },
    resetUserData(clearLocalStorage) {
        if (clearLocalStorage) {
            localStorage.clear();
        }

        this.user.reset = true; // tells the app localstorage has been reset. Primarily used to kow where to route when the app loads.
        this.user.currentStepName = 'Start';
        this.user.currentStepIndex = 0;
        // this.user.firstname = '';
        // this.user.lastname = '';
        // this.user.email = '';
        // this.user.company = '';
        this.user.projectname = '';
        this.user.roomname = '';
        this.user.room.width = 0;
        this.user.room.length = 0;
        this.user.room.height = 0;
        // this.user.room.unit = 'imperial';
        this.user.room.materials = [];
        this.user.room.usage = '';
        this.user.room.usagetarget = '';
        this.clearProducts();
        this.user.numproducts = '';
        this.user.user_id = '';
        this.user.company_id = '';
        this.user.walls_nrc = '';
        this.user.ceiling_nrc = '';
        this.user.floor_nrc = '';
        this.user.reportGenerated = false;
        this.user.imgUploadId = '';

        //
        // Keeping the following commented (for now).
        // Idea is to reset the values based on their type
        // however it needs some thought on whether is the
        // correct approach.
        //
        // this.user.roomunit = 'imperial';
        // this.user.currentStepName = 'Start';
        // this.user.currentStepIndex = 0;
        // this.user.roommaterials = [];
        // this.user.productdata = {};
        // const skipItems = [
        //     'roomunit',
        //     'currentStepName',
        //     'currentStepIndex',
        //     'roommaterials',
        //     'productdata',
        // ];
        // Object.keys(this.user).forEach((key) => {
        //     if (skipItems.indexOf(key) === -1) {
        //         $log.log(`typeof ${key} : ${JSON.stringify(typeof this.user[key], null, 4)}`);
        //         $log.log(`key : ${JSON.stringify(key, null, 4)} - ${skipItems.indexOf(key)}`);
        //         if (typeof this.user[key] === 'string') {
        //             this.user[key] = '';
        //         } else if (typeof this.user[key] === 'number') {
        //             this.user[key] = 0;
        //         } else if (typeof this.user[key] === 'object') {
        //             this.user[key] = {};
        //         } else {
        //             this.user[key] = '';
        //         }
        //     }
        // });
        // $log.log(`this.user : ${JSON.stringify(this.user, null, 4)}`);
    },

    /*
	------------------------------------------------------------------------
	Navigation actions
	------------------------------------------------------------------------
	*/
    setCurrentStepName(name) {
        this.user.currentStepName = name;
    },
    setStepStatus(stepName, newStatus) {
        this.steps.forEach((step) => {
            if (step.name === stepName) {
                step.status = this.statusFlags[newStatus];
                // $log.log(`step : ${JSON.stringify(step, null, 4)}`);
            }
        });
    },
    gotoStep: async function (stepName, router, direction, source) {
        $log.log(`[store-actions:gotoStep] stepName: ${stepName}, direction: ${direction}, source: ${source}`);

        // When navigating back to Product step, clear products so user starts fresh
        if ((direction === 'previous' || direction === 'prev') && stepName === 'Product') {
            this.clearProducts();
        }

        let isValid = true;
        //
        // Add step validation checks here.
        // Remember, we are validating the current step here, not the step we are trying to go to.
        //
        if (direction === 'next') {
            $log.log(`[store-actions:gotoStep] Direction is 'next'`);

            //
            // User can always go backwards, even if the step they are working in is not yet valid.
            // So here we only check validation if the direcion is 'next'.
            //
            isValid = this.validation[this.user.currentStepName].valid;
            $log.log(`[store-actions:gotoStep] Current step: ${this.user.currentStepName}, validation state:`, this.validation[this.user.currentStepName]);

            this.overlays.loading = true;

            if (stepName === 'Start') {
                isValid = Validations.validateStart();
            }
        }

        $log.log(`[store-actions:gotoStep] isValid: ${JSON.stringify(isValid, null, 4)}`);

        if (!isValid) {
            $log.log(`[store-actions:gotoStep] isValid is false so not able to proceed to the requested step.`);
            $log.log(`[store-actions:gotoStep] Validation errors:`, this.validation[this.user.currentStepName].errors);

            //
            // Not able to procees to the requested step as something did not validate.
            // $log.log(`step ${this.user.currentStepName} did not validate.`);
            //

            this.overlays.loading = true;

            // Show any validation messages in the UI.
            if (source === 'click') {
                this.validation[this.user.currentStepName].showErrors = true;
                if (this.user.currentStepName === 'Product') {
                    // $log.log(`showing product select modal`);
                    // TODO Needs to be tidied up ideally.
                    // Setting this directly here is not ideal because it shows the modal regardless of wether another is already
                    // open. We are handling this in StepProduct by explicility closing it if the product modal is open, but
                    // this means there could potentially be unintended behaviour in the future.
                    this.overlays.productvalidation.open = true;
                }
            }
            this.overlays.loading = false;
        } else {
            //
            // Step validated so we can proceed.
            // $log.log(`step ${this.user.currentStepName} validated.`);

            // Loading indicator.
            this.overlays.loading = true;

            // Hide any validation messages in the UI.
            this.validation[this.user.currentStepName].showErrors = false;

            //
            // Attempt to create the user at HubSpot.
            // Runs at every step change if the user id has not been set (in the client).
            //
            let createContactResponse = true;
            if (!this.user.user_id && direction !== 'previous') {
                createContactResponse = await createContact(this);
            }

            if (createContactResponse) {
                let stepFound = false;
                this.steps.forEach((step, index) => {
                    // Set steps before the target step as "complete".
                    if (!stepFound && step.name !== stepName) {
                        step.status = this.statusFlags.COMPLETE;
                    }

                    // Set the target step as "current".
                    if (step.name === stepName) {
                        step.status = this.statusFlags.CURRENT;
                        this.user.currentStepName = step.name;
                        this.user.currentStepIndex = index;

                        if (step.name !== 'Start') {
                            // Tells the app that the user has made data changes and they are in localstorge.
                            this.user.reset = false;
                        }

                        if (router) {
                            // When going back to Product step, don't include product ID in URL
                            const isGoingBackToProduct = step.name === 'Product' && (direction === 'previous' || direction === 'prev');
                            const params = isGoingBackToProduct ? {} : { id: this.user.productid };
                            router.push({ name: step.name, params });
                        }
                        // That the current step has been found.
                        stepFound = true;
                    }

                    // Set steps after the target step as "upcoming".
                    if (stepFound && step.name !== stepName) {
                        step.status = this.statusFlags.UPCOMING;
                    }
                });

                this.overlays.loading = false;
            }
        }
    },
};
